import React, { FC, lazy } from 'react'

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
  useParams,
  Params,
} from 'react-router-dom'

import { lazy as loadable } from '@loadable/component'

import { EmbedContext } from '@newComponents/Embed'
import { wrapSuspenseComponent } from '@newComponents/SuspenseWrapper'

const GuildCardEmbed = wrapSuspenseComponent(
  loadable(() => import('@embeds/Guild/GuildCardEmbed'))
)

const GuildEventLatest = wrapSuspenseComponent(
  loadable(() => import('@embeds/Guild/GuildEventLatest'))
)

const GuildEventList = wrapSuspenseComponent(
  loadable(() => import('@embeds/Guild/GuildEventList'))
)

const GuildPresentationLatest = wrapSuspenseComponent(
  loadable(() => import('@embeds/Guild/GuildPresentationLatest'))
)

const GuildPresentationList = wrapSuspenseComponent(
  loadable(() => import('@embeds/Guild/GuildPresentationList'))
)

const UserCardEmbed = wrapSuspenseComponent(
  loadable(() => import('@embeds/User/UserCardEmbed'))
)

const UserEventLatest = wrapSuspenseComponent(
  loadable(() => import('@embeds/User/UserEventLatest'))
)

const UserEventList = wrapSuspenseComponent(
  loadable(() => import('@embeds/User/UserEventList'))
)

const UserPresentationLatest = wrapSuspenseComponent(
  loadable(() => import('@embeds/User/UserPresentationLatest'))
)

const UserPresentationList = wrapSuspenseComponent(
  loadable(() => import('@embeds/User/UserPresentationList'))
)

const EventCardEmbed = wrapSuspenseComponent(
  loadable(() => import('@embeds/Event/EventCardEmbed'))
)

const EventItemEmbed = wrapSuspenseComponent(
  loadable(() => import('@embeds/Event/EventItemEmbed'))
)

const PresentationCardEmbed = wrapSuspenseComponent(
  loadable(() => import('@embeds/Presentation/PresentationCardEmbed'))
)

const PresentationItemEmbed = wrapSuspenseComponent(
  loadable(() => import('@embeds/Presentation/PresentationItemEmbed'))
)

interface ParamsContainerProps {
  children: (params: Record<string, string>) => JSX.Element
}

const ParamsContainer = ({ children }: ParamsContainerProps) => {
  const params = useParams()

  // Filter out undefined values - route params are guaranteed to exist when the route matches
  const definedParams = Object.fromEntries(
    Object.entries(params).filter((entry): entry is [string, string] => entry[1] !== undefined)
  )

  return children(definedParams)
}

export const EmbedNavigator: FC = () => {
  const location = useLocation()

  const locationState = location.state as { backgroundLocation?: Location }

  return (
    <EmbedContext.Provider value={true}>
      <Routes location={locationState?.backgroundLocation || location}>
        <Route
          path='guild/:guildId/card'
          element={
            <ParamsContainer>
              {({ guildId }) => <GuildCardEmbed slug={guildId} />}
            </ParamsContainer>
          }
        />

        <Route
          path='guild/:guildId/events/latest'
          element={
            <ParamsContainer>
              {({ guildId }) => <GuildEventLatest slug={guildId} />}
            </ParamsContainer>
          }
        />

        <Route
          path='guild/:guildId/events/upcoming'
          element={
            <ParamsContainer>
              {({ guildId }) => (
                <GuildEventList slug={guildId} timeSelection='UPCOMING' />
              )}
            </ParamsContainer>
          }
        />

        <Route
          path='guild/:guildId/events/past'
          element={
            <ParamsContainer>
              {({ guildId }) => (
                <GuildEventList slug={guildId} timeSelection='PAST' />
              )}
            </ParamsContainer>
          }
        />

        <Route
          path='guild/:guildId/presentations/latest'
          element={
            <ParamsContainer>
              {({ guildId }) => <GuildPresentationLatest slug={guildId} />}
            </ParamsContainer>
          }
        />

        <Route
          path='guild/:guildId/presentations/upcoming'
          element={
            <ParamsContainer>
              {({ guildId }) => (
                <GuildPresentationList
                  slug={guildId}
                  timeSelection='UPCOMING'
                />
              )}
            </ParamsContainer>
          }
        />

        <Route
          path='guild/:guildId/presentations/other'
          element={
            <ParamsContainer>
              {({ guildId }) => (
                <GuildPresentationList slug={guildId} timeSelection='OTHER' />
              )}
            </ParamsContainer>
          }
        />

        <Route
          path='user/:userId/card'
          element={
            <ParamsContainer>
              {({ userId }) => <UserCardEmbed slug={userId} />}
            </ParamsContainer>
          }
        />

        <Route
          path='user/:userId/events/latest'
          element={
            <ParamsContainer>
              {({ userId }) => <UserEventLatest slug={userId} />}
            </ParamsContainer>
          }
        />

        <Route
          path='user/:userId/events/upcoming'
          element={
            <ParamsContainer>
              {({ userId }) => (
                <UserEventList slug={userId} timeSelection='UPCOMING' />
              )}
            </ParamsContainer>
          }
        />

        <Route
          path='user/:userId/events/past'
          element={
            <ParamsContainer>
              {({ userId }) => (
                <UserEventList slug={userId} timeSelection='PAST' />
              )}
            </ParamsContainer>
          }
        />

        <Route
          path='user/:userId/presentations/latest'
          element={
            <ParamsContainer>
              {({ userId }) => <UserPresentationLatest slug={userId} />}
            </ParamsContainer>
          }
        />

        <Route
          path='user/:userId/presentations/upcoming'
          element={
            <ParamsContainer>
              {({ userId }) => (
                <UserPresentationList slug={userId} timeSelection='UPCOMING' />
              )}
            </ParamsContainer>
          }
        />

        <Route
          path='user/:userId/presentations/other'
          element={
            <ParamsContainer>
              {({ userId }) => (
                <UserPresentationList slug={userId} timeSelection='OTHER' />
              )}
            </ParamsContainer>
          }
        />

        <Route
          path='event/:eventId/card'
          element={
            <ParamsContainer>
              {({ eventId }) => <EventCardEmbed slug={eventId} />}
            </ParamsContainer>
          }
        />

        <Route
          path='event/:eventId/item'
          element={
            <ParamsContainer>
              {({ eventId }) => <EventItemEmbed slug={eventId} />}
            </ParamsContainer>
          }
        />

        <Route
          path='presentation/:presentationId/card'
          element={
            <ParamsContainer>
              {({ presentationId }) => (
                <PresentationCardEmbed slug={presentationId} />
              )}
            </ParamsContainer>
          }
        />

        <Route
          path='presentation/:presentationId/item'
          element={
            <ParamsContainer>
              {({ presentationId }) => (
                <PresentationItemEmbed slug={presentationId} />
              )}
            </ParamsContainer>
          }
        />
      </Routes>
    </EmbedContext.Provider>
  )
}

export default EmbedNavigator
